import { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | TaleCraft";
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="mt-1 text-sm text-gray-500">Effective Date: April 1, 2025</p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6 prose max-w-none text-gray-900">
            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">1. Introduction</h2>
              <p className="mb-4 text-gray-900">Welcome to TaleCraft. Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you use our platform.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">2. Information We Collect</h2>
              <p className="mb-2 text-gray-900">We collect the following types of data:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-900">
                <li><strong>Personal Information:</strong> Name, email address, account details.</li>
                <li><strong>Usage Data:</strong> Interactions with features, preferences, and submitted forms.</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information.</li>
                <li><strong>Cookies and Tracking:</strong> Session tracking, advertising cookies, and analytics for platform improvement.</li>
                <li><strong>Issue Reporting Data:</strong> Information submitted through our form-based issue reporting system.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">3. How We Use Your Information</h2>
              <p className="mb-2 text-gray-900">We use your data to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-900">
                <li>Provide and enhance the TaleCraft user experience.</li>
                <li>Secure accounts and prevent unauthorized access.</li>
                <li>Offer customer support and resolve reported issues.</li>
                <li>Communicate important updates regarding our services.</li>
                <li>Analyze platform performance and improve functionality.</li>
                <li>Deliver relevant advertisements to free tier users.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">4. Advertising and Google AdSense</h2>
              <p className="mb-2 text-gray-900">For users with free subscriptions, we display advertisements using Google AdSense:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-900">
                <li><strong>How AdSense works:</strong> Google AdSense uses cookies to serve personalized ads based on your browsing history and interests.</li>
                <li><strong>Data collection:</strong> Google collects data including your IP address, device information, and browsing behavior to deliver relevant ads.</li>
                <li><strong>Third-party vendors:</strong> Google, as a third-party vendor, uses cookies to serve ads on our site.</li>
                <li><strong>Personalized advertising:</strong> Google's use of advertising cookies enables it and its partners to serve ads based on your visit to our site and/or other sites on the Internet.</li>
                <li><strong>Opt-out options:</strong> You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads Settings</a>. Alternatively, you can opt out of a third-party vendor's use of cookies for personalized advertising by visiting <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.aboutads.info</a>.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">5. Data Storage & Sharing</h2>
              <p className="mb-2 text-gray-900">We do not sell your personal data. However, we may share information with:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-900">
                <li><strong>Supabase:</strong> For database storage and authentication services.</li>
                <li><strong>Payment Processors (e.g., Stripe):</strong> To handle transactions securely.</li>
                <li><strong>Analytics Providers (e.g., Google Analytics):</strong> To track user engagement and optimize the platform.</li>
                <li><strong>Advertising Partners (e.g., Google AdSense):</strong> To display advertisements to free tier users.</li>
              </ul>
              <p className="mb-4 text-gray-900">All data is processed and stored securely in accordance with applicable regulations.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">6. Data Retention & Deletion</h2>
              <p className="mb-4 text-gray-900">We retain user data for as long as necessary to provide our services. Users may request deletion of their data by contacting us. However, some data may be retained for security, legal, or operational purposes.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">7. Security Measures</h2>
              <p className="mb-4 text-gray-900">We implement technical and organizational measures to protect your personal information. However, no system is 100% secure, and we encourage users to take appropriate precautions.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">8. Your Rights</h2>
              <p className="mb-2 text-gray-900">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-900">
                <li>Access, modify, or delete your personal data.</li>
                <li>Opt-out of certain data processing activities.</li>
                <li>Request data portability for personal information stored with us.</li>
                <li>Opt-out of personalized advertising via Google's Ad Settings and industry tools.</li>
              </ul>
              <p className="mb-4 text-gray-900">To exercise these rights, please contact us at <strong>podpora@tref.si</strong>.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">9. Changes to This Policy</h2>
              <p className="mb-4 text-gray-900">We may update this Privacy Policy as our services evolve. Continued use of TaleCraft after any changes indicates your acceptance of the updated terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-2 text-gray-900">10. Contact Information</h2>
              <p className="mb-4 text-gray-900">For questions regarding this Privacy Policy, please contact us at <strong>podpora@tref.si</strong>.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
